import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBrands,
  getBrand,
  createBrand,
  updateBrand,
  deleteBrand,
  testSendBrandEmailConfig,
  testSendBrandMetaCapi,
} from "../services/brandApi";
import { CreateBrandDto, UpdateBrandDto } from "../types/brand";

export const useBrandsQuery = () => {
  return useQuery({
    queryKey: ["brands"],
    queryFn: getBrands,
  });
};

export const useBrandQuery = (brandId: string) => {
  return useQuery({
    queryKey: ["brands", brandId],
    queryFn: () => getBrand(brandId),
    enabled: !!brandId,
  });
};

export const useCreateBrandMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBrandDto) => createBrand(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
    },
  });
};

export const useUpdateBrandMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      brandId,
      data,
    }: {
      brandId: string;
      data: UpdateBrandDto;
    }) => updateBrand(brandId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
    },
  });
};

export const useDeleteBrandMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (brandId: string) => deleteBrand(brandId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
    },
  });
};

// F1 test-send: no cache invalidation needed — result is ephemeral, shown inline.
export const useTestSendEmailConfigMutation = () => {
  return useMutation({
    mutationFn: ({
      brandId,
      targetEmail,
    }: {
      brandId: string;
      targetEmail: string;
    }) => testSendBrandEmailConfig(brandId, targetEmail),
  });
};

// Meta CAPI test-send: ephemeral result, shown inline (same as email test-send).
export const useTestSendMetaCapiMutation = () => {
  return useMutation({
    mutationFn: ({
      brandId,
      testEventCode,
    }: {
      brandId: string;
      testEventCode: string;
    }) => testSendBrandMetaCapi(brandId, testEventCode),
  });
};
